/**
 * Rôles dérivés — règles pures (ADR 0006).
 *
 * Ce qu'un rôle défini a le droit de porter comme permissions, et pourquoi
 * pas. Partagées entre l'API — qui refuse — et l'écran — qui grise la case
 * avant qu'on ne la coche, avec le même motif.
 */
import { CODES_CHEF_DE_CENTRE, PERMISSIONS, PERMISSIONS_PAR_ROLE, ROLES_MEDICAUX, ROLES_NON_OUVERTS, estSuperAdmin, matriceDuCode, motifRoleNonOuvert, } from '../constants/roles.js';
/**
 * RG-SEC-ROLE-MEDICAL — le dossier médical suit la qualité de médecin, pas
 * le réglage d'un écran (RG-PA-07, CCT § 5.2).
 *
 *   dossier_medical:write   base médicale seulement
 *   dossier_medical:read    base médicale, ou la Direction (lecture, CCT § 2.1)
 */
export function motifMedical(base, permission) {
    if (permission === 'dossier_medical:write' && !ROLES_MEDICAUX.includes(base)) {
        return 'Le dossier médical ne s’écrit que depuis un rôle de base médical (RG-PA-07).';
    }
    if (permission === 'dossier_medical:read' &&
        !ROLES_MEDICAUX.includes(base) &&
        base !== 'DIRECTION') {
        return 'Le dossier médical ne se lit que depuis un rôle de base médical, ou la Direction (RG-PA-07).';
    }
    return null;
}
/**
 * RG-SEC-ROLE-SAUVEGARDE — une archive de sauvegarde contient toute la base
 * et le `.env` du serveur : elle ne se télécharge que depuis le rôle NATIF
 * Super administrateur (décisions 96 et 98, revue S128). Aucun rôle dérivé ne
 * la reçoit, même de base Super administrateur : sans cela, la Direction
 * pourrait la cocher sur un dérivé de base Super admin (la matrice de la base
 * la contient, RG-SEC-ROLE-ELEVATION laisserait passer).
 */
export function motifSauvegarde(permission, codeNatif) {
    if (permission === 'sauvegarde:telecharger' && codeNatif !== 'SUPER_ADMIN') {
        return 'Le téléchargement des sauvegardes est réservé au Super administrateur.';
    }
    return null;
}
/**
 * RG-SEC-ROLE-CENTRE — gérer les comptes d'un centre ne s'accorde qu'aux
 * deux rôles NATIFS du chef de centre, « Médecin + chef de centre » et
 * « Chef de centre » (décision 449), sur le modèle de la sauvegarde. Aucun
 * dérivé libre ne la reçoit, d'aucune base, même coché par le Super admin
 * qui la détient : un dérivé de MEDECIN qui la porterait ferait un chef sans
 * passer par la désignation du site, qui attribue le rôle.
 */
export function motifCentre(permission, codeNatif) {
    if (permission === 'utilisateur:write_centre' &&
        (codeNatif === undefined || !CODES_CHEF_DE_CENTRE.includes(codeNatif))) {
        return 'La gestion des comptes d’un centre est réservée aux rôles de responsable d’antenne.';
    }
    return null;
}
/**
 * RG-SEC-ROLE-SALAIRE — la paie des adhérents ne se lit que depuis la
 * chargée de clientèle, la Comptable ou la Direction (décision 130 du TL) :
 * « personne d'autre, Super admin compris ». Un dérivé d'une autre base ne la
 * reçoit pas, même de la Direction, qui la détient (RG-SEC-ROLE-ELEVATION
 * laisserait passer).
 */
export const BASES_SALAIRE = ['DIRECTION', 'COMPTABLE', 'CHARGE_CLIENTELE'];
export function motifSalaire(base, permission) {
    if (permission === 'salaire:read' && !BASES_SALAIRE.includes(base)) {
        return 'Les salaires ne se lisent que depuis un rôle de base chargée de clientèle, Comptable ou Direction.';
    }
    return null;
}
/**
 * RG-SEC-ROLE-ACTIVITE — l'activité nominative des médecins (patients reçus,
 * durée de leurs consultations) ne se lit que depuis un rôle de base médical
 * ou la Direction (décisions 165 et 168). Ce sont des chiffres de travail de
 * salariés : un dérivé de la Comptable ou de l'accueil ne la reçoit pas,
 * même accordée par la Direction, qui la détient.
 */
export const BASES_ACTIVITE_MEDECIN = [...ROLES_MEDICAUX, 'DIRECTION'];
export function motifActiviteMedecin(base, permission) {
    if (permission === 'activite_medecin:read' && !BASES_ACTIVITE_MEDECIN.includes(base)) {
        return 'L’activité des médecins ne se lit que depuis un rôle de base médical ou la Direction.';
    }
    return null;
}
/**
 * RG-SEC-ROLE-EXPORT-ENTREPRISE — le registre d'un mois pour une entreprise
 * porte les jours de repos médical remis à l'employeur (décision 458) : il
 * ne s'exporte que depuis un rôle de base chargée de clientèle ou Direction.
 * Un dérivé d'une autre base ne le reçoit pas, même de la Direction, qui le
 * détient (RG-SEC-ROLE-ELEVATION laisserait passer).
 */
export const BASES_EXPORT_ENTREPRISE = ['CHARGE_CLIENTELE', 'DIRECTION'];
export function motifExportEntreprise(base, permission) {
    if (permission === 'registre:export_entreprise' && !BASES_EXPORT_ENTREPRISE.includes(base)) {
        return 'L’export du registre d’une entreprise ne se donne qu’à un rôle de base chargée de clientèle ou Direction.';
    }
    return null;
}
/**
 * Les cases cochées d'office à la création d'un dérivé : celles de sa base.
 * La lecture des salaires n'est cochée d'office que pour un dérivé de la
 * chargée de clientèle (décision 142 du TL), à qui le Super admin peut la
 * retirer comme toute permission. Un dérivé de la Comptable ou de la
 * Direction naît sans : elle s'y coche, en connaissance de cause.
 */
export function permissionsInitialesDuDerive(base) {
    // RG-SEC-ROLE-CENTRE : un dérivé du chef de centre naît sans la gestion des
    // comptes, qu'aucun dérivé ne reçoit.
    const matrice = PERMISSIONS_PAR_ROLE[base].filter((permission) => motifCentre(permission) === null);
    if (base === 'CHARGE_CLIENTELE')
        return matrice;
    return matrice.filter((permission) => permission !== 'salaire:read');
}
/**
 * La matrice du code qui borne un rôle : celle de sa base, sauf pour un
 * dérivé NATIF (`codeNatif`, ADR 0006 révisée le 16/10), qui a la sienne.
 * Sans cette exception, la Direction — qui ne détient pas
 * `caisse:encaisser` — ne pourrait ni régler « Accueil + caisse » ni le
 * ramener à sa matrice. Un dérivé libre n'a pas de `codeNatif` : sa borne
 * reste la matrice de sa base.
 */
function matriceDeReference(base, codeNatif) {
    return (codeNatif === undefined ? null : matriceDuCode(codeNatif)) ?? PERMISSIONS_PAR_ROLE[base];
}
/**
 * Ce qu'un rôle de cette base peut recevoir, accordé par quelqu'un qui
 * détient `editeur`.
 *
 * RG-SEC-ROLE-ELEVATION — on ne donne pas ce qu'on n'a pas : une permission
 * s'accorde si la matrice du code la donne au rôle, OU si celui qui
 * l'accorde la détient lui-même. Le verrou médical prime sur les deux.
 */
export function permissionsAccordables(base, editeur, codeNatif) {
    if (ROLES_NON_OUVERTS.includes(base))
        return [];
    const candidates = new Set([...matriceDeReference(base, codeNatif), ...editeur]);
    return [...candidates].filter((permission) => motifMedical(base, permission) === null &&
        motifSauvegarde(permission, codeNatif) === null &&
        motifCentre(permission, codeNatif) === null &&
        motifSalaire(base, permission) === null &&
        motifActiviteMedecin(base, permission) === null &&
        motifExportEntreprise(base, permission) === null);
}
/**
 * Pourquoi une permission ne peut PAS être accordée à ce rôle par cet
 * éditeur — `null` si elle le peut. C'est le texte du survol de la case
 * grisée, et le message du refus serveur : le même.
 */
export function motifDeRefus(base, permission, editeur, codeNatif) {
    const nonOuvert = motifRoleNonOuvert(base);
    if (nonOuvert !== null)
        return { regle: 'RG-SEC-ROLE-NON-OUVERT', motif: nonOuvert };
    const medical = motifMedical(base, permission);
    if (medical !== null)
        return { regle: 'RG-SEC-ROLE-MEDICAL', motif: medical };
    const sauvegarde = motifSauvegarde(permission, codeNatif);
    if (sauvegarde !== null)
        return { regle: 'RG-SEC-ROLE-SAUVEGARDE', motif: sauvegarde };
    const centre = motifCentre(permission, codeNatif);
    if (centre !== null)
        return { regle: 'RG-SEC-ROLE-CENTRE', motif: centre };
    const salaire = motifSalaire(base, permission);
    if (salaire !== null)
        return { regle: 'RG-SEC-ROLE-SALAIRE', motif: salaire };
    const activite = motifActiviteMedecin(base, permission);
    if (activite !== null)
        return { regle: 'RG-SEC-ROLE-ACTIVITE', motif: activite };
    const exportEntreprise = motifExportEntreprise(base, permission);
    if (exportEntreprise !== null) {
        return { regle: 'RG-SEC-ROLE-EXPORT-ENTREPRISE', motif: exportEntreprise };
    }
    if (!matriceDeReference(base, codeNatif).includes(permission) && !editeur.includes(permission)) {
        return {
            regle: 'RG-SEC-ROLE-ELEVATION',
            motif: `Vous ne détenez pas « ${permission} » : on ne donne pas ce qu’on n’a pas.`,
        };
    }
    return null;
}
/**
 * Le premier refus d'un jeu de permissions complet, ou `null`.
 *
 * Un rôle de base Super administrateur ne se modifie jamais, et aucun ne se
 * crée : c'est le dernier recours de la plateforme — un Super administrateur
 * qui se retirerait `utilisateur:write` fermerait l'administration à tout le
 * monde — et, depuis la décision 418, il détient TOUT par calcul et il est
 * unique. Un dérivé de lui n'aurait rien à régler et ne s'attribuerait à
 * personne d'autre.
 */
export function validerPermissions(role, demandees, editeur) {
    if (estSuperAdmin(role.base)) {
        return {
            regle: 'RG-SEC-ROLE-SUPER_ADMIN',
            motif: role.natif
                ? 'Le rôle Super administrateur ne se modifie pas : c’est le dernier recours de la plateforme.'
                : 'Aucun rôle ne se dérive du Super administrateur : il est unique et détient déjà tout.',
        };
    }
    for (const permission of demandees) {
        const refus = motifDeRefus(role.base, permission, editeur, role.natif ? role.code : undefined);
        if (refus !== null)
            return refus;
    }
    return null;
}
/**
 * Les permissions effectives d'un rôle défini : la matrice du code tant
 * qu'un natif n'a pas été personnalisé, ce qui a été réglé sinon.
 *
 * C'est ce qui permet à une permission ajoutée au code dans une PR
 * d'atteindre les rôles natifs au déploiement suivant, sans migration.
 */
export function permissionsEffectives(role) {
    // Décision 418 — le Super administrateur détient tout, par calcul : ni une
    // personnalisation ancienne ni une ligne oubliée en base ne le restreignent.
    // Un dérivé de sa base resté en base (d'avant la 418) ne reçoit PAS tout :
    // ce qui lui a été coché, jamais ce qu'un verrou interdit (revue S1041, A2).
    if (estSuperAdmin(role.base)) {
        if (role.natif)
            return PERMISSIONS;
        return role.permissions.filter((permission) => motifMedical(role.base, permission) === null &&
            motifSauvegarde(permission) === null &&
            motifCentre(permission) === null &&
            motifSalaire(role.base, permission) === null &&
            motifActiviteMedecin(role.base, permission) === null &&
            motifExportEntreprise(role.base, permission) === null);
    }
    // Un natif personnalisé ou un dérivé garde en base ce qui a été coché :
    // un rôle non ouvert n'en rend rien, quoi qu'il porte (décision 115).
    if (ROLES_NON_OUVERTS.includes(role.base))
        return [];
    if (role.natif && !role.personnalise)
        return matriceDeReference(role.base, role.code);
    // RG-SEC-ROLE-CENTRE : une ligne écrite à la main en base (SQL, ancien
    // export) ne fait pas un chef de centre d'un rôle qui n'en est pas un.
    return role.permissions.filter((permission) => motifCentre(permission, role.natif ? role.code : undefined) === null);
}
/**
 * Le code d'un rôle dérivé, tiré de son libellé : majuscules, sans accent,
 * mots séparés par un tiret bas. « Accueil & PEC » → `ACCUEIL_PEC`.
 *
 * Un code se calcule, il ne se tape pas (skill `ux-ecrans-afs`) : il
 * identifie le rôle dans le jeton et le journal pour toujours, une faute de
 * frappe y resterait. Un libellé qui ne laisse rien rend `ROLE` — au serveur
 * d'y accrocher un suffixe.
 */
export function codeDeRole(libelle) {
    const noyau = libelle
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40)
        .replace(/_+$/, '');
    if (noyau === '' || !/^[A-Z]/.test(noyau))
        return `ROLE${noyau === '' ? '' : `_${noyau}`}`;
    return noyau;
}
/** La première variante libre : le code, puis `_2`, `_3`… */
export function codeDeRoleLibre(base, pris) {
    const occupes = new Set([...pris].map((code) => code.toUpperCase()));
    if (!occupes.has(base))
        return base;
    for (let suffixe = 2; suffixe <= 1000; suffixe++) {
        const candidat = `${base.slice(0, 60 - String(suffixe).length - 1)}_${suffixe}`;
        if (!occupes.has(candidat))
            return candidat;
    }
    throw new Error(`Aucun code libre pour « ${base} »`);
}
//# sourceMappingURL=roles-derives.js.map